import { useState } from "react";
import { useTellescopeMutation, useTellescopeQueryClient } from "@tellescope/tanstack-query";
import type { EnduserSession } from "@tellescope/sdk";

/** A cart price has no Tellescope Product configured for it, so it can't be charged. */
export class UnconfiguredShopProductError extends Error {}

export interface UnresolvedCartItem {
  stripePriceId: string;
  quantity: number;
}

/**
 * Resolves each cart item's selected Stripe price ID to its Tellescope Product _id via one batched
 * reverse-lookup (products.getSome), then calls prepare_stripe_checkout with `items` — one
 * `{ productId, quantity }` entry per cart line. Endusers may only send productId items; the
 * backend caps quantity at 99 and rejects quantities above 1 on recurring products.
 * Run at the point checkout is initiated, not earlier, since a resolved _id could go stale if
 * held longer. Keyed by stripePriceId, not stripeProductId — each price variant of a product maps
 * to its own distinct Tellescope Product record, so the specific variant the enduser selected is
 * what determines which Product gets charged.
 *
 * stripeKey (the shop block's configured Stripe Custom account, if any) is passed through
 * unconditionally, even when undefined — the backend auto-resolves the account when omitted (the
 * enduser's saved key, or the org's sole integration), erroring only if that's ambiguous.
 *
 * The returned `error` and `isPending` cover the Product lookup as well as the checkout call, so a
 * lookup failure reaches the UI the same way a checkout failure does.
 *
 * session is passed in rather than read from an app-local store — this package doesn't own the
 * enduser session, the consuming app (portal-web) does, so the caller (ShopBlock.tsx, via
 * PaymentSummary) resolves and passes it down.
 */
export const usePrepareShopCheckout = (session: EnduserSession | null) => {
  const queryClient = useTellescopeQueryClient(session ?? undefined);

  const checkoutMutation = useTellescopeMutation({
    model: "products",
    method: "prepare_stripe_checkout",
    session: session ?? undefined,
    autoCacheUpdateStrategy: "disabled",
  });

  const [lookupError, setLookupError] = useState<Error | null>(null);
  const [isResolving, setIsResolving] = useState(false);

  const resolveCheckoutItems = async (items: UnresolvedCartItem[]) => {
    const stripePriceIds = [...new Set(items.map((item) => item.stripePriceId))];
    const result = await queryClient.fetchInfiniteQuery({
      model: "products",
      method: "getSome",
      // Only the first page is read, and the API's default page is 100 records.
      request: { filter: { stripePriceId: { _in: stripePriceIds } }, limit: 1000 },
      initialPageParam: undefined,
      getNextPageParam: () => undefined,
    });

    // getSome returns newest first, so when several Products share a price the newest one is charged.
    const productIdByStripePriceId = new Map<string, string>();
    for (const product of result.pages.flat()) {
      if (product.stripePriceId && !productIdByStripePriceId.has(product.stripePriceId)) {
        productIdByStripePriceId.set(product.stripePriceId, product.id);
      }
    }

    const unresolved = stripePriceIds.filter((stripePriceId) => !productIdByStripePriceId.has(stripePriceId));
    if (unresolved.length > 0) {
      throw new UnconfiguredShopProductError(`No matching product configured for: ${unresolved.join(", ")}`);
    }

    return items.flatMap((item) => {
      const productId = productIdByStripePriceId.get(item.stripePriceId);
      return productId ? [{ productId, quantity: item.quantity }] : [];
    });
  };

  const resolveAndPrepareCheckout = async (items: UnresolvedCartItem[], stripeKey: string | undefined) => {
    checkoutMutation.reset();
    setLookupError(null);
    setIsResolving(true);
    let checkoutItems: { productId: string; quantity: number }[];
    try {
      checkoutItems = await resolveCheckoutItems(items);
    } catch (error) {
      setLookupError(error instanceof Error ? error : new Error(String(error)));
      throw error;
    } finally {
      setIsResolving(false);
    }
    return checkoutMutation.mutateAsync({ items: checkoutItems, stripeKey });
  };

  return {
    ...checkoutMutation,
    error: lookupError ?? checkoutMutation.error,
    isPending: isResolving || checkoutMutation.isPending,
    resolveAndPrepareCheckout,
  };
};
